import { ref, computed } from 'vue'

import { useAdaStore } from '../stores/ada'
import { usePageStore } from '../stores/page'
import { useClient } from '../utils/client-only'
import { useAdaSizePredictionCss } from './use-ada-size-prediction-css'
import { useDeviceDetection } from './use-device-detection'
import { useUrlHelper } from './use-url-helper'
import { useProductCardTracking } from './use-product-card-tracking'
import { useAdaMoltenBundle } from './use-ada-molten-bundle'
import { useAdaSlotLoader } from './use-ada-slot-loader'
import { useAdaDynamicSlotLoader } from './use-ada-dynamic-slot-loader'

import type { ComposedMetaData, ComposedPageMeta } from '@xcel-next/composer-schema'

type PageContentProps = {
  metaData?: ComposedMetaData,
  pageMeta?: ComposedPageMeta,
  taboolaEnabled?: boolean
}

export function usePageContentSharedSetup (props: PageContentProps) {
  const { isClient } = useClient()
  const { getAdSizePredictionStyleData } = useAdaSizePredictionCss(() => props.metaData)

  const adaStore = useAdaStore()
  const pageStore = usePageStore()

  const deviceDetection = useDeviceDetection()
  const urlHelper = useUrlHelper()
  const productCardTracking = useProductCardTracking(() => props.metaData)

  const registerSlots = ref<string[]>([])

  const enableNcbInDynamicMode = computed(() => !!(
    !import.meta.env.SSR &&
    !pageStore.pageData?.metaData?.disallowNativeTeasers &&
    !props.metaData?.advertorial &&
    !props.metaData?.disallowNativeTeasers &&
    !props.metaData?.isVideoOnlyArticle &&
    (pageStore.isArticleOrArticleVersion || props.metaData?.behaveLikeArticle) &&
    adaStore.dynamicAdSlotsEnabled
  ))

  const moltenBundle = useAdaMoltenBundle(
    { metaData: () => props.metaData, pageMeta: () => props.pageMeta, registerSlots, deviceType: deviceDetection.deviceType })

  const adaSlotLoader = useAdaSlotLoader(
    () => props.metaData,
    registerSlots,
    deviceDetection.deviceType,
    deviceDetection.screenOrientation,
    moltenBundle.initMoltenBundle
  )

  const dynamicSlotLoader = useAdaDynamicSlotLoader(
    () => props.metaData,
    () => props.taboolaEnabled ?? false,
    enableNcbInDynamicMode,
    registerSlots,
    moltenBundle.initMoltenBundle
  )

  return {
    isClient,
    getAdSizePredictionStyleData,
    registerSlots,
    enableNcbInDynamicMode,
    ...deviceDetection,
    ...urlHelper,
    ...productCardTracking,
    ...moltenBundle,
    ...adaSlotLoader,
    ...dynamicSlotLoader
  }
}
